/**
 * ensureLegibleText — post-write contrast audit for sandboxed email iframes.
 *
 * Task 4053e: email HTML in the Stream history / message panels must always be
 * legible while preserving the email's own design wherever it is safe to do
 * so. After `HtmlEmailFrame` writes the email document, this pass walks the
 * rendered elements, computes the WCAG contrast ratio between each element's
 * effective text colour and effective background, and corrects ONLY the
 * combinations that fall below 4.5:1. Legible combinations are never touched.
 *
 * Pure helpers (parseCssColor, relativeLuminance, contrastRatio,
 * resolveEffectiveBackground) are exported for unit testing without an iframe.
 *
 * Safety:
 *  - Bounded: documents with more than ELEMENT_CAP elements fall back to a
 *    blanket dark-on-white override so legibility is guaranteed even when
 *    fidelity cannot be.
 *  - Any unexpected throw also triggers the blanket fallback.
 *  - Read/writes happen on the already-written document via contentDocument;
 *    no scripts are injected and the iframe sandbox is unchanged.
 */

// ── Types & constants ────────────────────────────────────────────────────────

export interface RgbaColor {
  r: number
  g: number
  b: number
  /** 0–1 */
  a: number
}

export interface EffectiveBackground {
  /** Composited, fully opaque background colour (alpha is always 1). */
  color: RgbaColor
  /** True when any element in the background chain paints a background-image. */
  hasBackgroundImage: boolean
}

export interface EnsureLegibleTextOptions {
  /** Element cap before falling back to the blanket override. */
  maxElements?: number
}

/** WCAG AA threshold for normal text. */
export const MIN_CONTRAST_RATIO = 4.5

/**
 * Below this ratio against the colour painted beneath a background-image
 * (composited chain colour, white fallback), text over an unknowable
 * background-image is considered near-invisible and corrected anyway.
 */
export const NEAR_INVISIBLE_RATIO = 1.8

/** Default element cap (task 4053e: "cap ~2,000 elements"). */
export const DEFAULT_ELEMENT_CAP = 2000

export const DARK_TEXT = '#1a1a1a'
export const LIGHT_TEXT = '#f5f5f5'
export const DARK_LINK = '#1a73e8'
export const LIGHT_LINK = '#8ab4f8'

const WHITE: RgbaColor = { r: 255, g: 255, b: 255, a: 1 }

const BLANKET_STYLE_ID = 'kdn-legibility-fallback'

/** Elements that never carry user-visible text we should audit. */
const SKIPPED_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'HEAD',
  'META',
  'LINK',
  'TITLE',
  'NOSCRIPT',
  'TEMPLATE',
])

// ── Pure colour helpers ──────────────────────────────────────────────────────

const NAMED_COLORS: Record<string, RgbaColor> = {
  white: { r: 255, g: 255, b: 255, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
  transparent: { r: 0, g: 0, b: 0, a: 0 },
}

/**
 * Parse a CSS colour string into RGBA. Supports rgb()/rgba(), 3/4/6/8-digit
 * hex, and a minimal keyword set. Returns null for anything unparseable
 * (gradients, var(), currentcolor, exotic keywords) — callers treat null as
 * "unknown, leave alone".
 */
export function parseCssColor(value: string | null | undefined): RgbaColor | null {
  if (!value) return null
  const v = value.trim().toLowerCase()

  if (v in NAMED_COLORS) return { ...NAMED_COLORS[v] }

  // Legacy comma syntax and modern space/slash syntax (browsers may serialize
  // either form): rgb(1, 2, 3) / rgba(1, 2, 3, .5) / rgb(1 2 3) / rgb(1 2 3 / .5)
  const rgbMatch = v.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+%?)\s*)?\)$/,
  ) ?? v.match(
    /^rgba?\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.]+%?)\s*)?\)$/,
  )
  if (rgbMatch) {
    const a = rgbMatch[4] === undefined
      ? 1
      : rgbMatch[4].endsWith('%')
        ? parseFloat(rgbMatch[4]) / 100
        : parseFloat(rgbMatch[4])
    return {
      r: clamp255(parseFloat(rgbMatch[1])),
      g: clamp255(parseFloat(rgbMatch[2])),
      b: clamp255(parseFloat(rgbMatch[3])),
      a: clamp01(a),
    }
  }

  const hexMatch = v.match(/^#([0-9a-f]{3,8})$/)
  if (hexMatch) {
    const hex = hexMatch[1]
    if (hex.length === 3 || hex.length === 4) {
      const r = parseInt(hex[0] + hex[0], 16)
      const g = parseInt(hex[1] + hex[1], 16)
      const b = parseInt(hex[2] + hex[2], 16)
      const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1
      return { r, g, b, a }
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
      return { r, g, b, a }
    }
  }

  return null
}

function clamp255(n: number): number {
  return Math.min(255, Math.max(0, Math.round(n)))
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n))
}

/** WCAG relative luminance of an (opaque) sRGB colour. */
export function relativeLuminance(color: RgbaColor): number {
  const channel = (c: number): number => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b)
}

/** WCAG contrast ratio between two opaque colours (1–21). */
export function contrastRatio(fg: RgbaColor, bg: RgbaColor): number {
  const l1 = relativeLuminance(fg)
  const l2 = relativeLuminance(bg)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

/** Alpha-composite `top` over fully opaque `bottom`. Result is opaque. */
export function compositeOver(top: RgbaColor, bottom: RgbaColor): RgbaColor {
  const a = clamp01(top.a)
  return {
    r: clamp255(top.r * a + bottom.r * (1 - a)),
    g: clamp255(top.g * a + bottom.g * (1 - a)),
    b: clamp255(top.b * a + bottom.b * (1 - a)),
    a: 1,
  }
}

/**
 * Resolve the effective background behind an element: walk ancestors,
 * collecting non-transparent background-color layers (alpha-aware), flag any
 * background-image in the chain, and fall back to the frame's white canvas
 * when nothing opaque is found.
 */
export function resolveEffectiveBackground(el: Element, doc: Document): EffectiveBackground {
  const win = doc.defaultView
  let hasBackgroundImage = false
  /** Nearest-first translucent layers above the opaque base. */
  const layers: RgbaColor[] = []
  let base: RgbaColor | null = null

  let node: Element | null = el
  while (node && node !== doc.documentElement.parentElement) {
    let bgColor: string | null = null
    let bgImage: string | null = null
    try {
      const cs = win ? win.getComputedStyle(node) : null
      bgColor = cs ? cs.backgroundColor : null
      bgImage = cs ? cs.backgroundImage : null
    } catch {
      bgColor = null
      bgImage = null
    }
    // Fall back to inline styles when computed style is unavailable/empty.
    // Duck-typed (not `instanceof HTMLElement`): iframe elements belong to a
    // different realm, so cross-realm instanceof checks always fail.
    const inlineStyle = (node as HTMLElement).style as CSSStyleDeclaration | undefined
    if (!bgColor && inlineStyle) bgColor = inlineStyle.backgroundColor
    if (!bgImage && inlineStyle) bgImage = inlineStyle.backgroundImage

    if (bgImage && bgImage !== 'none') hasBackgroundImage = true

    const parsed = parseCssColor(bgColor)
    if (parsed && parsed.a > 0) {
      if (parsed.a >= 1) {
        base = parsed
        break
      }
      layers.push(parsed)
    }
    node = node.parentElement
  }

  let result = base ?? WHITE
  // Composite translucent layers bottom-up (farthest first).
  for (let i = layers.length - 1; i >= 0; i--) {
    result = compositeOver(layers[i], result)
  }
  return { color: result, hasBackgroundImage }
}

// ── Driver ───────────────────────────────────────────────────────────────────

/** True when the element owns at least one non-whitespace direct text node. */
function hasDirectText(el: Element): boolean {
  for (const child of el.childNodes) {
    if (child.nodeType === 3 /* TEXT_NODE */ && (child.nodeValue ?? '').trim() !== '') {
      return true
    }
  }
  return false
}

/**
 * Blanket guaranteed-legible override: dark text on white, transparent
 * intermediate backgrounds, reasserted link/code/SVG styling.
 *
 * This is the authoritative legibility enforcement function. It does not use
 * getComputedStyle() anywhere — all operations are synchronous DOM mutations
 * that work reliably in WKWebView (Tauri) immediately after doc.write().
 *
 * Three-part strategy:
 *
 * 1. Inject a !important stylesheet that forces dark text / white background
 *    on all body descendants. Beats normal author stylesheets and email
 *    <style> blocks (which appear earlier in source order).
 *
 * 2. Strip inline style declarations that carry !important priority for
 *    color / background-color. Per the CSS cascade spec, inline !important
 *    beats author-stylesheet !important — so these must be removed rather
 *    than overridden via CSS alone. Non-important inline declarations are
 *    already overridden by the !important stylesheet and do not need stripping.
 *
 * 3. Force SVG text fill to dark so SVG <text fill="white"> is legible.
 *    The CSS `color` property does not affect SVG fill when fill is set
 *    explicitly as an attribute or presentation property.
 */
export function applyBlanketOverride(doc: Document): void {
  if (doc.getElementById(BLANKET_STYLE_ID)) return

  // ── 1. Inject !important stylesheet ────────────────────────────────────────
  const style = doc.createElement('style')
  style.id = BLANKET_STYLE_ID
  style.textContent = `
    body { background: #ffffff !important; }
    body, body * { color: ${DARK_TEXT} !important; background-color: transparent !important; }
    a, a * { color: ${DARK_LINK} !important; text-decoration: underline !important; }
    pre, code { background-color: #f5f5f5 !important; }
    svg text, svg tspan, svg textPath { fill: ${DARK_TEXT} !important; }
  `
  const parent = doc.head ?? doc.documentElement
  if (parent) parent.appendChild(style)

  // ── 2. Strip inline !important color/background-color declarations ─────────
  // Inline !important declarations outrank author-stylesheet !important in the
  // CSS cascade and cannot be overridden by CSS alone. We remove the priority
  // flag rather than the property so the value itself remains (harmlessly
  // overridden by the stylesheet above after removal).
  try {
    doc.querySelectorAll('[style]').forEach((el) => {
      const htmlEl = el as HTMLElement
      const s = htmlEl.style
      if (!s) return
      if (s.getPropertyPriority('color') === 'important') {
        const val = s.getPropertyValue('color')
        s.removeProperty('color')
        s.setProperty('color', val) // rewrite without !important
      }
      if (s.getPropertyPriority('background-color') === 'important') {
        const val = s.getPropertyValue('background-color')
        s.removeProperty('background-color')
        s.setProperty('background-color', val) // rewrite without !important
      }
    })
  } catch {
    // If the querySelectorAll walk throws for any reason (malformed document,
    // cross-origin quirk), the stylesheet injected in step 1 still provides
    // substantial coverage. Silently continue.
  }
}

function auditElement(el: HTMLElement, doc: Document): void {
  const win = doc.defaultView
  let colorValue: string | null = null
  try {
    colorValue = win ? win.getComputedStyle(el).color : null
  } catch {
    colorValue = null
  }
  if (!colorValue) colorValue = el.style.color || null

  const fg = parseCssColor(colorValue)
  // Unknown or fully transparent text colour: leave alone (cannot judge).
  if (!fg || fg.a === 0) return

  const bg = resolveEffectiveBackground(el, doc)

  if (bg.hasBackgroundImage) {
    // Unknowable effective background. Judge against the colour that paints
    // BENEATH the image — the composited chain colour when one exists (e.g. a
    // dark email setting both background-color and a texture image), else the
    // frame's white fallback. Skip unless the text would be near-invisible
    // against that under-image colour; correcting against blanket white here
    // would flip legible dark designs (white text on #111 + texture) into
    // dark-on-dark.
    const effectiveFg = fg.a < 1 ? compositeOver(fg, bg.color) : fg
    if (contrastRatio(effectiveFg, bg.color) >= NEAR_INVISIBLE_RATIO) return
    applyBestContrastColor(el, bg.color)
    return
  }

  const effectiveFg = fg.a < 1 ? compositeOver(fg, bg.color) : fg
  if (contrastRatio(effectiveFg, bg.color) >= MIN_CONTRAST_RATIO) return

  applyBestContrastColor(el, bg.color)
}

/**
 * Illegible: pick whichever standard colour maximises contrast against the
 * actual effective background. Links get link colours to stay distinct.
 */
function applyBestContrastColor(el: HTMLElement, bgColor: RgbaColor): void {
  const isLink = el.closest('a') !== null
  const darkChoice = parseCssColor(isLink ? DARK_LINK : DARK_TEXT) as RgbaColor
  const lightChoice = parseCssColor(isLink ? LIGHT_LINK : LIGHT_TEXT) as RgbaColor
  const useDark = contrastRatio(darkChoice, bgColor) >= contrastRatio(lightChoice, bgColor)
  el.style.setProperty(
    'color',
    useDark ? (isLink ? DARK_LINK : DARK_TEXT) : (isLink ? LIGHT_LINK : LIGHT_TEXT),
    'important',
  )
}

/**
 * Contrast-audit the written email document, correcting only text/background
 * combinations below 4.5:1. Idempotent: corrected elements pass the audit on
 * subsequent runs, so re-running on iframe `load` is safe.
 */
export function ensureLegibleText(doc: Document, options?: EnsureLegibleTextOptions): void {
  const maxElements = options?.maxElements ?? DEFAULT_ELEMENT_CAP
  try {
    const body = doc.body
    if (!body) return

    const candidates: HTMLElement[] = []
    const walker = doc.createTreeWalker(body, 0x1 /* NodeFilter.SHOW_ELEMENT */)
    let visited = 0
    let node = walker.nextNode()
    while (node) {
      visited++
      if (visited > maxElements) {
        applyBlanketOverride(doc)
        return
      }
      const el = node as HTMLElement
      if (!SKIPPED_TAGS.has(el.tagName) && hasDirectText(el)) {
        candidates.push(el)
      }
      node = walker.nextNode()
    }

    for (const el of candidates) {
      auditElement(el, doc)
    }
  } catch {
    try {
      applyBlanketOverride(doc)
    } catch {
      // Document is unusable; nothing further we can do safely.
    }
  }
}
